// features/orders/components/OrderDetail.jsx
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Download, CreditCard, Trash2, Pencil, Search } from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import { Badge } from '../../../components/ui/Badge'
import { Skeleton } from '../../../components/ui/Skeleton'
import { ErrorState } from '../../../components/feedback/ErrorState'
import { QuickPayDrawer } from './QuickPayDrawer'
import { apiFetch, apiFetchBlob, downloadBlob } from '../../../api'
import { formatMoney, formatDate } from '../../../lib/format'
import { toastError, toastSuccess } from '../../../components/feedback/Toast'
import { useOrderPrefetch } from '../hooks/useOrderPrefetch'
import { useStore } from '../../../store/index.js'

function CorrectLineModal({ orderId, line, onClose, onSuccess }) {
  const [productSearch, setProductSearch] = useState('')
  const [productResults, setProductResults] = useState([])
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [unitPrice, setUnitPrice] = useState(String(Number(line.unit_price || 0)))
  const [qty, setQty] = useState(String(Number(line.qty || 1)))
  const [loadingProducts, setLoadingProducts] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const term = productSearch.trim()
    if (!term) {
      setProductResults([])
      return undefined
    }

    const timer = window.setTimeout(async () => {
      setLoadingProducts(true)
      try {
        const data = await apiFetch(`/api/products?search=${encodeURIComponent(term)}&limit=8`)
        setProductResults(Array.isArray(data?.rows) ? data.rows : Array.isArray(data) ? data : [])
      } catch {
        setProductResults([])
      } finally {
        setLoadingProducts(false)
      }
    }, 250)

    return () => window.clearTimeout(timer)
  }, [productSearch])

  function selectProduct(product) {
    setSelectedProduct(product)
    setProductSearch(product.name || '')
    setUnitPrice(String(Number(product.unit_price || 0)))
    setProductResults([])
  }

  async function handleSubmit() {
    setError('')
    const parsedPrice = Number(unitPrice)
    const parsedQty = Math.round(Number(qty))

    if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
      setError('Prix invalide')
      return
    }
    if (!parsedQty || parsedQty <= 0) {
      setError('Quantité invalide')
      return
    }

    const body = {}
    if (selectedProduct && selectedProduct.id !== line.product_id) body.product_id = selectedProduct.id
    if (parsedPrice !== Number(line.unit_price)) body.unit_price = parsedPrice
    if (parsedQty !== Number(line.qty)) body.qty = parsedQty

    if (!Object.keys(body).length) {
      setError('Aucune modification détectée')
      return
    }

    setSubmitting(true)
    try {
      await apiFetch(`/api/orders/${orderId}/lines/${line.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      await onSuccess?.()
    } catch (err) {
      setError(err?.message || 'Erreur lors de la correction')
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-slate-900">Corriger la ligne</h3>
            <p className="text-xs text-slate-500 mt-1">Produit, prix unitaire ou quantité</p>
          </div>
          <Button size="sm" variant="ghost" icon={X} onClick={onClose} />
        </div>

        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
          <div className="font-semibold text-slate-900">{line.product_name || 'Produit'}</div>
          <div className="mt-1 text-xs text-slate-500">
            {formatMoney(line.unit_price)} × {line.qty}
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}

        <div className="mt-4 space-y-4">
          <div className="relative">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Produit
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={productSearch}
                onChange={(e) => {
                  setProductSearch(e.target.value)
                  setSelectedProduct(null)
                }}
                placeholder={line.product_name || 'Rechercher un produit'}
                className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-purple-100"
              />
            </div>
            {loadingProducts && (
              <div className="mt-1 text-xs text-slate-500">Recherche en cours…</div>
            )}
            {productResults.length > 0 && (
              <div className="mt-2 max-h-52 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg">
                {productResults.map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => selectProduct(product)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-slate-50"
                  >
                    <span className="text-sm font-medium text-slate-900">{product.name}</span>
                    <span className="text-xs text-slate-500">
                      {formatMoney(product.unit_price)} · stock: {product.stock_quantity ?? '?'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Prix unitaire
              </label>
              <input
                type="number"
                min="0"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-purple-100"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Quantité
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-purple-100"
              />
            </div>
          </div>

          <div className="text-right text-sm text-slate-500">
            Sous-total estimé:{' '}
            <span className="font-bold text-slate-900">
              {formatMoney(Number(unitPrice || 0) * Math.round(Number(qty || 1)))}
            </span>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button variant="accent" onClick={handleSubmit} loading={submitting}>
            Appliquer
          </Button>
        </div>
      </div>
    </div>
  )
}

export function OrderDetail({ id, onClose, onDeleted, onUpdated }) {
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [quickPayOpen, setQuickPayOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [correctingLine, setCorrectingLine] = useState(null)
  const navigate = useNavigate()
  const { getFromCache, invalidate } = useOrderPrefetch()
  const user = useStore((s) => s.user)
  const canDelete = Array.isArray(user?.roles) && user.roles.some((r) => ['admin', 'superadmin'].includes(r))
  const canCorrect = canDelete

  async function loadDetailById(orderId, { preferCache = true } = {}) {
    setLoading(true)
    setError(null)

    try {
      if (preferCache) {
        const cached = getFromCache(orderId)
        if (cached) {
          setOrder(cached)
          setLoading(false)
          return
        }
      }

      const data = await apiFetch(`/api/orders/${orderId}`)
      setOrder(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (id) {
      loadDetailById(id)
    }
  }, [id, getFromCache])

  async function handleDownloadPDF() {
    try {
      const blob = await apiFetchBlob(`/api/orders/${order.external_id || id}/pdf`)
      downloadBlob(blob, `facture_${order.external_id || id}.pdf`)
    } catch (err) {
      toastError('Erreur téléchargement', err.message)
    }
  }

  async function handleDelete() {
    if (!order) return
    const ok = window.confirm(
      'Voulez-vous vraiment supprimer cette vente ? Cette action supprimera également la facture et les paiements associés.'
    )
    if (!ok) return

    setDeleting(true)
    try {
      await apiFetch(`/api/orders/${order.external_id || id}`, { method: 'DELETE' })
      toastSuccess('Vente supprimée', `Vente ${order.external_id || id} supprimée`)
      onDeleted?.()
      onClose?.()
    } catch (err) {
      toastError('Erreur suppression', err.message)
    } finally {
      setDeleting(false)
    }
  }

  async function handleLineCorrectionSuccess() {
    const orderKey = order?.external_id || id
    invalidate(orderKey)
    if (id && orderKey !== id) invalidate(id)
    setCorrectingLine(null)
    await loadDetailById(orderKey, { preferCache: false })
    onUpdated?.()
  }

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          title="Erreur chargement"
          description={error}
          onRetry={() => window.location.reload()}
        />
      </div>
    )
  }

  if (!order) {
    return (
      <div className="p-6">
        <ErrorState title="Vente introuvable" />
      </div>
    )
  }

  const lines = Array.isArray(order.lines) ? order.lines : []
  const hasUnpaid = order.payment?.remaining > 0
  const mode = String(order.payment_mode || order.payment?.mode || '').toLowerCase()
  const isInstallments = ['installment', 'installments'].includes(mode)
  const planId =
    order.payment_plan?.id ||
    order.payment?.plan_id ||
    order.payment?.payment_plan_id ||
    order.payment?.planId ||
    order.payment?.paymentPlanId ||
    null
  const canViewInstallments = !!planId && isInstallments

  return (
    <>
      <div className="h-full overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black text-slate-900">
              {order.external_id || order.id}
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Créée le {formatDate(order.created_at)}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {order.external_id && (
              <Button
                size="sm"
                variant="secondary"
                icon={Download}
                onClick={handleDownloadPDF}
              >
                PDF
              </Button>
            )}
            {hasUnpaid && !isInstallments && (
              <Button
                size="sm"
                variant="accent"
                icon={CreditCard}
                onClick={() => setQuickPayOpen(true)}
              >
                Encaisser
              </Button>
            )}
            {canDelete && (
              <Button
                size="sm"
                variant="danger"
                icon={Trash2}
                onClick={handleDelete}
                loading={deleting}
              >
                Supprimer
              </Button>
            )}
            {onClose && (
              <Button size="sm" variant="ghost" icon={X} onClick={onClose} />
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Client */}
          <section>
            <h3 className="text-sm font-bold uppercase text-slate-500 mb-3">Client</h3>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="font-semibold text-slate-900">
                {order.buyer?.name || order.buyer_name || 'Non renseigné'}
              </div>
              {(order.buyer?.phone || order.buyer_phone) && (
                <div className="text-sm text-slate-600 mt-1">
                  {order.buyer?.phone || order.buyer_phone}
                </div>
              )}
            </div>
          </section>

          {/* Lignes */}
          <section>
            <h3 className="text-sm font-bold uppercase text-slate-500 mb-3">
              Articles ({lines.length})
            </h3>
            <div className="space-y-2">
              {lines.map((line, idx) => (
                <div
                  key={line.id || idx}
                  className="rounded-lg border border-slate-200 bg-white p-4 flex items-center justify-between"
                >
                  <div>
                    <div className="font-semibold text-slate-900">
                      {line.product_name || line.name || 'Produit'}
                    </div>
                    <div className="text-sm text-slate-600 mt-1">
                      {formatMoney(line.unit_price)} × {line.qty}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-lg font-bold text-slate-900">
                      {formatMoney(line.line_total || line.qty * line.unit_price)}
                    </div>
                    {canCorrect && order.payment_mode !== 'cancelled' && line.id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={Pencil}
                        onClick={(e) => {
                          e.stopPropagation()
                          setCorrectingLine(line)
                        }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Paiement */}
          {order.payment && (
            <section>
              <h3 className="text-sm font-bold uppercase text-slate-500 mb-3">Paiement</h3>
              <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Total TTC</span>
                  <span className="font-bold text-slate-900">
                    {formatMoney(order.payment.total_ttc)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Payé</span>
                  <span className="font-bold text-green-600">
                    {formatMoney(order.payment.paid_total || 0)}
                  </span>
                </div>
                <div className="flex justify-between text-sm pt-3 border-t border-slate-200">
                  <span className="text-slate-600">Reste</span>
                  <span className="font-bold text-rose-600">
                    {formatMoney(order.payment.remaining || 0)}
                  </span>
                </div>
                {canViewInstallments && (
                  <div className="pt-3 border-t border-slate-200 flex items-center justify-between">
                    <div className="text-sm text-slate-600">
                      Paiement échelonné ({order.payment?.installments_count || 0} échéances)
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        navigate(
                          `/installments?plan_id=${planId}${
                            order.customer_uid ? `&customer_uid=${order.customer_uid}` : ''
                          }`
                        )
                      }
                    >
                      Voir échéances
                    </Button>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      </div>

      {/* Quick Pay Drawer */}
      <QuickPayDrawer
        order={{ ...order, remaining: order.payment?.remaining || 0 }}
        open={quickPayOpen}
        onClose={() => setQuickPayOpen(false)}
        onSuccess={() => {
          // Recharger détail
          window.location.reload()
        }}
      />

      {correctingLine && (
        <CorrectLineModal
          orderId={order?.external_id || id}
          line={correctingLine}
          onClose={() => setCorrectingLine(null)}
          onSuccess={handleLineCorrectionSuccess}
        />
      )}
    </>
  )
}
